/**
 * `Slot` — the config-driven component resolver. Given a `Slot` config
 * item (`SlotProps.item`, `Slot` shape), resolves `item.componentId` against the injected
 * `SlotRegistry` (`resolveSlot`/`useSlotRegistry`) and renders whichever of three outcomes
 * applies:
 *  - no `componentId` at all (`item.componentId === undefined`, checked by strict equality — NOT
 *    truthiness — so a deliberately empty-string `componentId` is still treated as "has one" and
 *    goes through the resolve path rather than being silently coerced to the fallback case) → call
 *    and render the caller-supplied `fallback()` (e.g. `() => renderPlainText(value)` for a
 *    plain-text slot);
 *  - a `componentId` that resolves to nothing in the registry → a visible, accessible error (never a
 *    throw — `resolveSlot` itself never throws either, and a single misconfigured slot must not take
 *    down the whole shell) PLUS exactly one `console.error` naming the missing id, so the failure is
 *    loud in dev tools without being loud in the UI beyond one red inline marker; `fallback` is NEVER
 *    called on this path;
 *  - a `componentId` that resolves to a registered component → render it, forwarding `item`/`value`/
 *    `mode` verbatim (the same `SlotProps` shape `Slot` itself received); `fallback` is NEVER called
 *    on this path either.
 *
 * LAZY FALLBACK: `fallback` is a THUNK — `() => React.ReactNode` — not a plain
 * `React.ReactNode`, and it is invoked ONLY inside the `componentId === undefined` branch above,
 * never on either `componentId`-resolution path. A plain `React.ReactNode` prop is a JSX expression
 * the CALLER evaluates EAGERLY, during the host molecule's own render, before `Slot` ever runs — e.g.
 * `fallback={renderPlainText(value) ?? label}` calls `renderPlainText(value)` unconditionally, even
 * when `item.componentId` is set and about to resolve to a real, registered component.
 * `renderPlainText` THROWS on a non-primitive `value` (`slots/renderPlainText.ts`, by design), so a
 * slot with a VALID `componentId` bound to a structured (object/array) value used to crash the whole
 * host molecule instead of rendering the resolved component. Deferring the expression behind a thunk
 * means it only ever runs when `Slot` itself decides to fall back — never merely as a side effect of
 * being passed as a prop.
 *
 * Isolation: imports ONLY `react`, the `SlotProps` type (deep path `../../interface/slotProps`, not
 * the `interface` barrel), and `resolveSlot`/`useSlotRegistry` (deep path `../../registry`) — nothing
 * else from project_starburst or beyond, satisfying the boundary guard
 * (`../../__tests__/boundary.test.ts`).
 */
import React from 'react';
import type { SlotProps } from '../../interface/slotProps';
import { resolveSlot, useSlotRegistry } from '../../registry';

export const Slot: React.FC<
  SlotProps & { fallback: () => React.ReactNode }
> = ({ item, value, mode, fallback }) => {
  const registry = useSlotRegistry();
  if (item.componentId !== undefined) {
    const Comp = resolveSlot(registry, item.componentId);
    if (!Comp) {
      console.error(
        `project_starburst: componentId "${item.componentId}" not found in registry`
      );
      return (
        <span role="alert" style={{ color: 'var(--color-alert-500, #e50000)' }}>
          Component not found!
        </span>
      );
    }
    return <Comp item={item} value={value} mode={mode} />;
  }
  return <>{fallback()}</>;
};
